import React from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isJasonAdminEmail } from '../../auth/adminAccess';

export type EmployerNavKey =
  | 'dashboard'
  | 'reports'
  | 'candidate-links'
  | 'billing'
  | 'build-request'
  | 'simulation-library'
  | 'ai-interviews';

type EmployerTopNavProps = {
  activeSection?: EmployerNavKey;
  children?: React.ReactNode;
};

const getNavButtonClass = (active: boolean): string =>
  active
    ? 'rounded-[10px] border border-[#0d9488] bg-[#f0fdfa] px-3 py-2 text-[12px] font-semibold text-[#0d9488]'
    : 'rounded-[10px] border border-[#e2e5eb] bg-white px-3 py-2 text-[12px] font-semibold text-[#475569] transition-colors hover:border-[#0d9488] hover:text-[#0d9488]';

const getActiveSection = (pathname: string): EmployerNavKey | null => {
  if (pathname === '/employer/dashboard' || pathname === '/dashboard') return 'dashboard';
  if (pathname === '/employer/sim/results') return 'reports';
  if (pathname === '/sim/create-link') return 'candidate-links';
  if (pathname === '/employer/billing' || pathname.startsWith('/billing/')) return 'billing';
  if (pathname === '/employer/build-request') return 'build-request';
  if (pathname === '/simulation-library') return 'simulation-library';
  if (pathname.startsWith('/employer/ai-interviews')) return 'ai-interviews';
  return null;
};

const EmployerTopNav: React.FC<EmployerTopNavProps> = ({ activeSection, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const active = activeSection || getActiveSection(location.pathname);
  const isAdmin = isJasonAdminEmail(session?.user?.email);

  return (
    <nav className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => navigate('/employer/dashboard')}
        className={getNavButtonClass(active === 'dashboard')}
      >
        Dashboard
      </button>
      <button
        type="button"
        onClick={() => navigate('/employer/sim/results')}
        className={getNavButtonClass(active === 'reports')}
      >
        Reports
      </button>
      <button
        type="button"
        onClick={() => navigate('/sim/create-link')}
        className={getNavButtonClass(active === 'candidate-links')}
      >
        Candidate Links
      </button>
      <button
        type="button"
        onClick={() => navigate('/employer/billing')}
        className={getNavButtonClass(active === 'billing')}
      >
        Billing
      </button>
      <button
        type="button"
        onClick={() => navigate('/employer/build-request')}
        className={getNavButtonClass(active === 'build-request')}
      >
        Build Request
      </button>
      <button
        type="button"
        onClick={() => navigate('/simulation-library')}
        className={getNavButtonClass(active === 'simulation-library')}
      >
        Simulation Library
      </button>
      {isAdmin ? (
        <>
          <button
            type="button"
            onClick={() => navigate('/employer/ai-interviews')}
            className={getNavButtonClass(active === 'ai-interviews')}
          >
            AI Interviews
          </button>
          <button type="button" onClick={() => navigate('/employer/admin')} className={getNavButtonClass(false)}>
            Admin
          </button>
        </>
      ) : null}
      {children}
    </nav>
  );
};

export default EmployerTopNav;
